import { Link } from "react-router-dom";
import { useLocale, useLocalePath, useT } from "../i18n";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useSiteSettings } from "../lib/siteSettings";
import { PageHero } from "../components/forms/FormKit";
import { labelOf, useTaxonomies } from "../lib/taxonomies";

const Block = ({ index, title, children, testId }) => (
  <div data-testid={testId}>
  <Reveal className="grid gap-6 border-t border-ink/15 py-12 lg:grid-cols-12 lg:gap-10 lg:py-16">
    <div className="flex items-baseline gap-4 lg:col-span-4">
      <span className="micro-label tabular-nums text-ink/35">{index}</span>
      <h2 className="font-display text-3xl lg:text-4xl">{title}</h2>
    </div>
    <div className="lg:col-span-8">{children}</div>
  </Reveal>
  </div>
);

export default function ExportPage() {
  const t = useT();
  const lang = useLocale();
  const lp = useLocalePath();
  const { export_page: page, loaded } = useSiteSettings();
  const c = page?.[lang] || {};
  const steps = c.steps || [];
  const [tx] = useTaxonomies();
  const markets = page?.markets || {};
  return (
    <div data-testid="export-page">
      <Seo title={t("pages.export.title")} description={t("pages.export.description")} path="export" />
      <PageHero eyebrow={t("exportPage.eyebrow")} title={t("exportPage.title")} intro={c.intro} />
      <section className="bg-paper pb-8 text-ink" aria-busy={!loaded}>
        <div className="container-x">
          {steps.length > 0 && (
            <Block index="01" title={t("exportPage.process")} testId="export-steps">
              <ol className="grid gap-px bg-ink/10 sm:grid-cols-2">
                {steps.map((s, i) => (
                  <li key={i} className="bg-paper p-6 sm:p-8">
                    <p className="micro-label tabular-nums text-ink/40">{String(i + 1).padStart(2, "0")}</p>
                    <p className="font-display mt-3 text-2xl">{s.title}</p>
                    {s.body && <p className="mt-3 text-[15px] leading-relaxed text-ink/70">{s.body}</p>}
                  </li>
                ))}
              </ol>
            </Block>
          )}
          {c.documents?.length > 0 && (
            <Block index="02" title={t("exportPage.documents")} testId="export-documents">
              <ul className="border-t border-ink/15">
                {c.documents.map((d, i) => <li key={i} className="border-b border-ink/15 py-4 text-[15px]">{d}</li>)}
              </ul>
            </Block>
          )}
          {c.shipping?.length > 0 && (
            <Block index="03" title={t("exportPage.shipping")} testId="export-shipping">
              <div className="grid gap-8 sm:grid-cols-2">
                {c.shipping.map((s, i) => (
                  <div key={i} className="border-t border-ink pt-5">
                    <p className="font-display text-2xl">{s.title}</p>
                    <p className="mt-3 text-[15px] leading-relaxed text-ink/70">{s.body}</p>
                  </div>
                ))}
              </div>
            </Block>
          )}
          {loaded && (
            <Block index="04" title={t("exportPage.regions")} testId="export-regions">
              <p className="border-l-2 border-ink pl-4 text-[15px] leading-relaxed text-ink/80" data-testid="export-eligibility-note">{t("exportPage.eligibilityNote")}</p>
              <div className="mt-10 grid gap-px bg-ink/10 sm:grid-cols-2 xl:grid-cols-3">
                {(tx?.export_regions || []).map((r) => (
                  <div key={r.key} className="bg-paper p-6" data-testid={`export-region-${r.key}`}>
                    <p className="font-display text-2xl">{r.label}</p>
                    <p className="micro-label mt-4 text-ink/40">{t("exportPage.examples")}</p>
                    <p className="mt-2 text-[15px] leading-relaxed text-ink/70">
                      {(markets[r.key] || []).map((code) => labelOf(tx, "countries", code) || code).join(lang === "ja" ? "、" : ", ") || t("exportPage.regionsEmpty")}
                    </p>
                  </div>
                ))}
              </div>
              {c.regions?.length > 0 && <ul className="mt-8 flex flex-wrap gap-2">{c.regions.map((r, i) => <li key={i} className="border border-ink/20 px-4 py-2 text-sm">{r}</li>)}</ul>}
            </Block>
          )}
        </div>
      </section>
      <section className="bg-ink py-20 text-white lg:py-28" data-testid="export-cta">
        <div className="container-x grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <h2 className="h-section font-display">{t("exportPage.ctaTitle")}</h2>
            <p className="mt-5 max-w-md text-white/60">{t("exportPage.ctaBody")}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end">
            <Link to={lp("sourcing")} className="btn btn-light" data-testid="export-cta-sourcing">{t("exportPage.ctaSourcing")}</Link>
            <Link to={lp("contact")} className="btn btn-ghost-light" data-testid="export-cta-contact">{t("exportPage.ctaContact")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
